import DesignTokens from '@tableau/design-tokens';
import { TabStyles } from '@tableau/widgets';
import React from 'react';

import { L10n } from '~commonInfra/L10n/L10n';
import { CommonStyles } from '~commonInfra/styles/CommonStyles';
import { Messages } from '~src/codegen/Localize';
import { cssClass } from '~src/style';
import { applyColor } from '~src/style/ColorService';

import { TextTableCell } from '../../bridgeNetworkAllowlist/TextTableCell';
import { RoutingFilterItem } from '../../types/RoutingFilterItem';
import { RoutingFilterPermission } from '../../types/RoutingFilterPermission';
import { collectConflictingProjectIds, getDistinctConflictMessages } from '../../utils/RoutingFilterErrorUtilities';
import { getRoutingFilterWarnings } from '../../utils/RoutingFilterWarningUtilities';
import { useRoutingFiltersActions } from '../RoutingFiltersActionsContext';
import { useRoutingFiltersConflict } from '../RoutingFiltersConflictContext';
import { useRoutingFiltersWarning } from '../RoutingFiltersWarningContext';
import { DeleteRoutingFilterButton } from './DeleteRoutingFilterButton';
import { EditRoutingFilterButton } from './EditRoutingFilterButton';
import { ProjectsInfoPopover } from './ProjectsInfoPopover';
import { ERROR_LIGHT_FALLBACK } from './routingFilterTableStyles';
import { StatusBadgePopover, StatusBadgePopoverAction } from './StatusBadgePopover';

export type RoutingFilterTableRowProps = Readonly<{
  filter: RoutingFilterItem;
}>;

function getPermissionTypeLabel(filter: RoutingFilterItem): string {
  return filter.isBlocked
    ? Messages.bridgeSiteSettingsRoutingFilterPermissionBlock()
    : Messages.bridgeSiteSettingsRoutingFilterPermissionAllow();
}

function renderWithBoldDomain(message: string, domain: string): React.ReactElement {
  const idx = message.indexOf(domain);
  if (idx === -1) {
    return <>{message}</>;
  }
  return <>{message.substring(0, idx)}<strong>{domain}</strong>{message.substring(idx + domain.length)}</>;
}

function getDescriptionLabel(filter: RoutingFilterItem): React.ReactElement {
  if (filter.projects.length > 0) {
    return renderWithBoldDomain(
      Messages.bridgeSiteSettingsRoutingFilterDomainAndProjects({ DOMAIN_NAME: filter.domain, PROJECT_COUNT: String(filter.projects.length) }),
      filter.domain
    );
  }
  return renderWithBoldDomain(
    Messages.bridgeSiteSettingsRoutingFilterDomainIs({ DOMAIN_NAME: filter.domain }),
    filter.domain
  );
}

export function RoutingFilterTableRow(props: RoutingFilterTableRowProps) {
  const { filter } = props;
  const { onEditFilterClicked, onDeleteFilterClicked, onClearUnspecifiedProjectsClicked, onAddFilterClicked } = useRoutingFiltersActions();
  const { unsavedFilter, clearUnsavedFilter, conflictsHighlighted, showConflictingRoutingFilters } = useRoutingFiltersConflict();
  const { warnedDomains } = useRoutingFiltersWarning();
  const { hasMissingDomainWideWarning: hasDomainWarning, hasDeletedProjects } = getRoutingFilterWarnings(filter, warnedDomains);
  const warningCount = (hasDomainWarning ? 1 : 0) + (hasDeletedProjects ? 1 : 0);
  const showWarning = warningCount > 0;

  const isUnsaved = unsavedFilter?.filter.id === filter.id;
  const isConflicting = unsavedFilter?.conflictingFilterIds.has(filter.id) ?? false;
  // The unsaved row is always highlighted; the other conflicting rows only light
  // up once the user opens the conflict badge on the unsaved row.
  const rowClass = isUnsaved || (isConflicting && conflictsHighlighted) ? highlightedRowClass : 'tb-data-grid-separator-row';
  // Another filter is mid edit/conflict; row actions on this saved row are locked.
  const lockedByOtherUnsaved = !!unsavedFilter && !isUnsaved;

  const editCellRef = React.useRef<HTMLSpanElement>(null);
  // When the badge's action clears the last warning the whole popover unmounts,
  // so hand focus back to a stable element in the row (the edit button) rather
  // than letting it fall to document.body. Skip the edit button when it is
  // disabled (another filter is mid-edit) — focusing a disabled control is a
  // no-op, so returning null lets PopoverWidget fall back to the badge trigger.
  const refocusEditButton = React.useCallback(() => {
    const btn = editCellRef.current?.querySelector<HTMLButtonElement>('button');
    return btn && !btn.disabled ? btn : null;
  }, []);

  const conflictingProjectIds = React.useMemo(() => {
    if (!unsavedFilter || !(isUnsaved || isConflicting)) {
      return undefined;
    }
    const ids = collectConflictingProjectIds(unsavedFilter.conflictErrors);
    return ids.size > 0 ? ids : undefined;
  }, [unsavedFilter, isUnsaved, isConflicting]);

  // Distinct conflict messages for the popover bullets, deduped on the stable
  // error code. Shared with the dialog's conflict banner so both read identically.
  const conflictMessages = React.useMemo(
    () => getDistinctConflictMessages(unsavedFilter?.conflictErrors),
    [unsavedFilter]
  );
  // Count distinct conflicts, not raw errors: a single conflict reported against
  // several filters is one error, so the badge count matches the popover bullets.
  const errorCount = conflictMessages.length;
  const conflictTitle = Messages.bridgeSiteSettingsRoutingFilterConflictCount({ COUNT: errorCount, COUNT_LOC: L10n.localizedCount(errorCount) });
  const warningTitle = Messages.bridgeSiteSettingsRoutingFilterWarningCount({ COUNT: warningCount, COUNT_LOC: L10n.localizedCount(warningCount) });

  const conflictAction: StatusBadgePopoverAction = {
    label: Messages.discardChanges(),
    onClick: clearUnsavedFilter,
    testId: 'discard-unsaved-filter-button',
  };

  // A warning row can be actionable in two ways at once: a missing domain-wide
  // rule can be fixed by adding an Allow rule for this domain with no projects
  // (All Projects), and deleted projects can be cleared from the rule.
  const warningActions: StatusBadgePopoverAction[] = [];
  if (hasDomainWarning) {
    warningActions.push({
      label: Messages.bridgeSiteSettingsRoutingFilterAddDomainWideRule(),
      // Open the create dialog pre-filled with this domain as an Allow rule and
      // no projects selected — exactly the domain-wide rule the warning asks for.
      onClick: () => onAddFilterClicked({ domainName: filter.domain, permission: RoutingFilterPermission.ALLOW }),
      testId: 'add-domain-wide-rule-button',
      // Locked while another filter is being edited/resolved, so opening a new
      // create dialog can't silently discard that pending state.
      disabled: lockedByOtherUnsaved,
    });
  }
  if (hasDeletedProjects) {
    warningActions.push({
      label: Messages.bridgeSiteSettingsRoutingFilterClearUnspecifiedProjects(),
      onClick: () => onClearUnspecifiedProjectsClicked(filter.id),
      testId: 'clear-unspecified-projects-button',
      // Locked while another filter is being edited/resolved, so clearing
      // here can't silently discard that pending state.
      disabled: lockedByOtherUnsaved,
    });
  }

  return (
    // Per-row test hook: the description/permission/pool cells, the status badges,
    // and the edit/delete buttons all carry row-invariant testIds, so a headless
    // driver scopes to one filter's row via this id first
    // (e.g. [data-tb-test-id="routing-filter-row-<id>"] [data-tb-test-id="edit-routing-filter-button"]).
    // data-unsaved / data-conflicting expose the row's validation state without
    // reading theme class names or localized text. Absent (not "false") when off.
    <tr
      className={rowClass}
      data-tb-test-id={'routing-filter-row-' + filter.id}
      data-unsaved={isUnsaved || undefined}
      data-conflicting={isConflicting || undefined}
    >
      <td>
        <span className={'tb-data-grid-cell-text tb-lr-padded-wide'} data-tb-test-id={'routing-filter-description-cell'}>
          {getDescriptionLabel(filter)}
          {filter.projects.length > 0 && (
            <ProjectsInfoPopover projects={filter.projects} conflictingProjectIds={conflictingProjectIds} />
          )}
          {isUnsaved && <span className={unsavedBadgeClass}>{Messages.bridgeSiteSettingsRoutingFilterUnsaved()}</span>}
        </span>
      </td>
      <TextTableCell testId={'routing-filter-permission-cell'} text={getPermissionTypeLabel(filter)} isTextItalicized={false} />
      <TextTableCell testId={'routing-filter-pool-cell'} text={filter.poolName ?? ''} isTextItalicized={filter.isBlocked} />
      <td data-tb-test-id={'routing-filter-status-cell'} className={statusCellClass}>
        {isUnsaved && (
          <StatusBadgePopover
            variant='error'
            testId='conflict-popover'
            describedById={'conflictDescription-' + filter.id}
            count={errorCount}
            title={conflictTitle}
            body={
              <ul className={popoverListClass}>
                {conflictMessages.map(({ errorCode, message }) => (
                  <li key={errorCode}>{message}</li>
                ))}
              </ul>
            }
            getElementToRefocus={refocusEditButton}
            actions={[conflictAction]}
            onTriggerClick={showConflictingRoutingFilters}
          />
        )}
        {!isUnsaved && showWarning && (
          <StatusBadgePopover
            variant='warning'
            testId='routing-filter-warning-popover'
            describedById={'routingFilterWarningDescription-' + filter.id}
            count={warningCount}
            title={warningTitle}
            body={
              <ul className={popoverListClass}>
                {hasDomainWarning && (
                  <li>{Messages.bridgeSiteSettingsRoutingFilterMissingDomainWideRuleDescription()}</li>
                )}
                {hasDeletedProjects && (
                  <li>{Messages.bridgeSiteSettingsRoutingFilterDeletedProjectsDescription()}</li>
                )}
              </ul>
            }
            getElementToRefocus={refocusEditButton}
            actions={warningActions}
          />
        )}
      </td>
      <td>
        <span ref={editCellRef} className={'tb-data-grid-cell-text tb-lr-padded-wide ' + editCellClass}>
          <EditRoutingFilterButton onClick={() => onEditFilterClicked(filter.id)} disabled={lockedByOtherUnsaved} />
          {!(isUnsaved && unsavedFilter?.isNew) && (
            <DeleteRoutingFilterButton onClick={() => onDeleteFilterClicked(filter.id)} disabled={lockedByOtherUnsaved} />
          )}
        </span>
      </td>
    </tr>
  );
}

const statusCellClass = cssClass('routing-filter-status-cell', 'root', {
  position: 'relative',
});

const editCellClass = cssClass('routing-filter-edit-cell', 'root', {
  float: 'right'
});

// Applied to both the unsaved row and (once revealed) its conflicting rows —
// they share the same error-tinted highlight. The alert-background token is
// theme-aware — a light pink fill in light theme, a dark maroon in dark theme —
// so the row's own theme-aware cell text (dark on light, light on dark) stays
// legible in both without a pinned foreground color, matching how InlineAlert
// surfaces render. This also keeps the row's <img> action icons readable, since
// their contrast now comes from a theme-appropriate fill rather than a fixed one.
const highlightedRowClass = cssClass('routing-filter-highlighted-row', 'root', {
  backgroundColor: applyColor(DesignTokens.color.component.background.alert.default.error.default, ERROR_LIGHT_FALLBACK),
  borderBottom: '1px solid ' + applyColor(DesignTokens.color.elevation.border.default.subtle, CommonStyles.Colors.DividerLineColor),
});

const unsavedBadgeClass = cssClass('routing-filter-unsaved-badge', 'root', {
  marginLeft: TabStyles.Spacing.SmallSpace,
  padding: `2px ${TabStyles.Spacing.SmallSpace}px`,
  fontSize: TabStyles.Typography.Sizing.Small,
  fontWeight: 'bold',
  borderRadius: 4,
  backgroundColor: applyColor(DesignTokens.color.component.background.header.default.info, '#e8f4fd'),
  color: applyColor(DesignTokens.color.feedback.default.info, '#05628a'),
});

// Shared bullet-list style for the warning and conflict popover bodies.
const popoverListClass = cssClass('routing-filter-popover', 'list', {
  margin: 0,
  paddingLeft: TabStyles.Spacing.DefaultSpace,
  $nest: {
    '& > li + li': {
      marginTop: TabStyles.Spacing.DefaultSpace,
    },
  },
});
